<template>
	<!--
		close-delay="0" forces the overlay backdrop to tear down immediately on
		close instead of lingering through the trailing scale-transition window
		(~300ms). Without it the backdrop can absorb pointer events on the next
		input field in a stepper / form layout (root cause 2 of AAR-1318).
		Affects programmatic close (date-pick + away-click); the scale-out
		animation itself still plays.
	-->
	<v-menu
		v-model="menu"
		:close-on-content-click="false"
		transition="scale-transition"
		close-delay="0"
		:data-test="`${dataTestModule ? dataTestModule + '__' : ''}${dataTest ?? 'datepicker'}-datepicker-menu`"
		min-width="auto"
	>
		<template #activator="{ props: menuProps }">
			<v-text-field
				v-bind="menuProps"
				v-maska:formattedDate.unmasked="'##.##.####'"
				:model-value="formattedDate"
				:label="translatedLabel"
				:placeholder="placeholder ?? placeHolder"
				:hint="hint"
				:error-messages="errorMessage"
				:disabled="disabled"
				:readonly="readonly"
				:prepend-icon="icon"
				autocomplete="off"
				clearable
				@click:prepend="menu = !menu"
				@blur="onBlur"
				:data-test="`${dataTestModule ? dataTestModule + '__' : ''}${dataTest ?? 'datepicker'}-datepicker`"
				@update:model-value="onInput"
				@keydown.enter="onKeyEnter"
				@click:clear="onClear"
			/>
		</template>
		<v-date-picker
			v-model="localDate"
			:min="min"
			:max="max"
			:disabled-dates="disabledDates"
			:show-adjacent-months="showAdjacentMonths"
			:data-test="`${dataTestModule ? dataTestModule + '__' : ''}${dataTest ?? 'datepicker'}-datepicker-select`"
			:type="type"
			:color="color"
			title=""
			scrollable
			@update:model-value="onDatePickerChange"
		>
			<template #title>
				<div class="d-flex" style="justify-content: space-between">
					<span class="align-self-center">{{
						formattedDate ? formattedDate : translatedLabel
					}}</span>
					<v-spacer></v-spacer>
					<v-btn
						icon="$close"
						variant="text"
						data-test="close-btn"
						@click="menu = !menu"
					></v-btn>
				</div>
			</template>
		</v-date-picker>
	</v-menu>
</template>

<script lang="ts" setup>
import { vMaska } from 'maska/vue'
import { useField } from 'vee-validate'
import { computed, ref, toRef, watch } from 'vue'
import type { Dayjs } from 'dayjs'
import { useI18n } from 'vue-i18n'
import { throttle } from 'lodash' // Add this import

interface Props {
	modelValue?: Date | null
	label?: string
	placeholder?: string
	hint?: string
	name?: string
	disabled?: boolean
	readonly?: boolean
	firstDayOfWeek?: number
	min?: string
	max?: string
	disabledDates?: Array<string | Function>
	showAdjacentMonths?: boolean
	type?: 'date' | 'month'
	color?: string
	dateFormat?: string
	displayFormat?: string
	icon?: string
	rules?: Record<string, any>
	dataTest?: string
	dataTestModule?: string
}
const { t, te } = useI18n()

const props = withDefaults(defineProps<Props>(), {
	modelValue: null,
	label: '',
	placeholder: undefined,
	hint: '',
	name: 'date',
	disabled: false,
	readonly: false,
	firstDayOfWeek: 1,
	min: undefined,
	max: undefined,
	disabledDates: () => [],
	showAdjacentMonths: true,
	type: 'date',
	color: 'primary',
	dateFormat: 'yyyy-MM-dd',
	displayFormat: 'dd.MM.yyyy',
	icon: 'fa-light fa-calendar-day',
	rules: undefined,
	dataTest: undefined,
	dataTestModule: undefined,
})
// Vorher fix t('common.date') — damit hiessen alle Picker "Datum", auch mit
// eigenem label (von/bis waren nicht unterscheidbar). Ohne label bleibt es bei
// t('common.date'), also weiterhin lokalisiert.
const translatedLabel = computed(() => {
	if (!props.label) return t('common.date')
	return te(props.label) ? t(props.label) : props.label
})
const placeHolder = computed(() => t('common.selectDate'))

const emit = defineEmits(['update:model-value'])

const menu = ref(false)
const localDate = ref<Date | null>(props.modelValue ? props.modelValue : null)

const formattedDate = computed(() => {
	if (!localDate.value || isNaN(localDate.value.getTime())) {
		return ''
	}
	const d = localDate.value
	return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
})

const parseDate = (dateString: string): Date | null => {
	if (!dateString) return null
	const regex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/
	const match = dateString.match(regex)
	if (!match) return null

	const [_, day, month, year] = match
	const parsedDate = new Date(
		parseInt(year),
		parseInt(month) - 1,
		parseInt(day)
	)
	return isNaN(parsedDate.getTime()) ? null : parsedDate
}

// Build the field-level rules to pass to useField.
//
// When the caller provides an explicit `rules` prop, we normalise them and
// forward them so the field validates independently.
// When `rules` is absent, we return undefined so the enclosing useForm /
// step-level validationSchema drives validation.  Passing an empty object
// would silently override the step schema and suppress all cross-field rules
// (conditionalDateMin, conditionalDateMax, requiredIfCallback …).
const fieldRules = computed<Record<string, any> | undefined>(() => {
	const hasCallerRules = props.rules && Object.keys(props.rules).length > 0
	if (!hasCallerRules) return undefined

	const processedRules = { ...props.rules }

	// Normalise legacy date_min → conditionalDateMin
	if (
		props.rules.date_min &&
		typeof props.rules.date_min === 'object' &&
		props.rules.date_min.min
	) {
		processedRules.conditionalDateMin = {
			min: props.rules.date_min.min,
			condition: true,
		}
		delete processedRules.date_min
	}

	// Always enforce format validation when caller rules are present
	if (!processedRules.dateFormat) {
		processedRules.dateFormat = true
	}

	// Derive conditionalDateMin from :min prop when not already set by caller
	if (props.min && !processedRules.conditionalDateMin) {
		const minDate = new Date(props.min)
		if (!isNaN(minDate.getTime())) {
			const formattedMin = `${String(minDate.getDate()).padStart(2, '0')}.${String(minDate.getMonth() + 1).padStart(2, '0')}.${minDate.getFullYear()}`
			processedRules.conditionalDateMin = {
				min: formattedMin,
				condition: true,
			}
		}
	}

	return processedRules
})

const {
	value: fieldValue,
	errorMessage,
	handleBlur,
	validate,
	resetField,
} = useField(props.name, fieldRules, {
	label: toRef(props.label),
	validateOnValueUpdate: true,
	initialValue: props.modelValue ? formattedDate.value : '',
})

watch(fieldValue, (newValue) => {
	const parsedDate = parseDate(newValue)
	if (parsedDate) {
		localDate.value = parsedDate
		emit('update:model-value', parsedDate)
	} else if (!newValue) {
		localDate.value = null
		emit('update:model-value', null)
	}
})

watch(localDate, (newValue) => {
	fieldValue.value = newValue ? formattedDate.value : ''
	emit('update:model-value', newValue)
})

watch(
	() => props.modelValue,
	(newValue) => {
		if (newValue && !(newValue instanceof Date)) {
			localDate.value = new Date(newValue)
		} else {
			localDate.value = newValue
		}
	}
)

watch(
	() => props.rules,
	() => {
		validate()
	},
	{ deep: true }
)

const onDatePickerChange = (value: Dayjs | null) => {
	localDate.value = value ? value.toDate() : null
	fieldValue.value = value ? formattedDate.value : ''
	emit('update:model-value', localDate.value)
	menu.value = false
}

const onBlur = () => {
	handleBlur()
	validate()

	if (!localDate.value) {
		emit('update:model-value', null)
	}
}

const onKeyEnter = (event: KeyboardEvent) => {
	event.preventDefault() // optional, prevents form submission if in a form
	onInput((event.target as HTMLInputElement).value)
	menu.value = false
}

const throttledInput = throttle(
	(inputValue: string) => {
		const parsedDate = parseDate(inputValue)
		if (parsedDate) {
			localDate.value = parsedDate
			emit('update:model-value', parsedDate)
		} else if (!inputValue) {
			localDate.value = null
			emit('update:model-value', null)
		}
	},
	300,
	{ leading: true }
)

const onInput = (value: any) => {
	const inputValue = value
	fieldValue.value = inputValue
	validate()

	throttledInput(inputValue)
}

const onClear = () => {
	localDate.value = null
	fieldValue.value = ''
	resetField()
	emit('update:model-value', null)
}
</script>
